import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';

const LEVELS = [
  { label: 'Committed', key: 'level1', level: 1 },
  { label: 'Employer', key: 'level2', level: 2 },
  { label: 'Leader', key: 'level3', level: 3 },
];

// Score a single question from its stored answer (0 / 0.5 / 1)
function scoreQuestion(q, ans) {
  if (!ans) return 0;
  if (typeof ans === 'string') {
    if (ans === 'yes') return 1;
    if (ans === 'partial') return 0.5;
    return 0;
  }
  if (typeof ans === 'object') {
    if (ans.answer === 'yes') return 1;
    if (ans.answer === 'partial') return 0.5;
    if (ans.answer === 'no') return 0;
    // Fallback: derive from checked criteria vs sub_criteria
    if (Array.isArray(ans.checked) && q.sub_criteria) {
      const total = q.sub_criteria.length;
      const checked = ans.checked.length;
      if (total > 0 && checked === total) return 1;
      if (checked > 0) return 0.5;
      return 0;
    }
  }
  return 0;
}

// Ordered list of real themes, grouped by their primary level then question order
function getOrderedThemes(questions) {
  const minLevel = {};
  const minOrder = {};
  questions.forEach(q => {
    const lvl = q.level ?? 1;
    const ord = q.order ?? 0;
    if (!(q.theme in minLevel) || lvl < minLevel[q.theme]) minLevel[q.theme] = lvl;
    if (!(q.theme in minOrder) || ord < minOrder[q.theme]) minOrder[q.theme] = ord;
  });
  return Object.keys(minLevel).sort((a, b) =>
    (minLevel[a] - minLevel[b]) || ((minOrder[a] || 0) - (minOrder[b] || 0))
  );
}

// Derive per-theme, per-level scores from the latest assessment's real answers
function buildHeatmapData(assessments, questions) {
  if (!questions || questions.length === 0) return [];
  const themes = getOrderedThemes(questions);

  // No assessment yet — every cell is not-applicable
  if (!assessments || assessments.length === 0) {
    return themes.map(theme => ({ theme, level1: null, level2: null, level3: null }));
  }

  const latest = assessments[0];
  const answers = latest.answers || {};

  // Group questions by theme
  const byTheme = {};
  questions.forEach(q => {
    if (!byTheme[q.theme]) byTheme[q.theme] = [];
    byTheme[q.theme].push(q);
  });

  // Detect whether stored answers can still be matched to current questions.
  // Questions can be re-seeded with new IDs, orphaning older answer keys.
  const questionIds = new Set(questions.map(q => q.id));
  const answersAreUsable = Object.keys(answers).some(k => questionIds.has(k));

  return themes.map(theme => {
    const row = { theme, level1: null, level2: null, level3: null };
    LEVELS.forEach(({ key, level }) => {
      const levelQs = (byTheme[theme] || []).filter(q => q.level === level);
      if (levelQs.length === 0) return; // theme has no questions at this level → stays null (N/A)
      if (answersAreUsable) {
        let earned = 0;
        let total = 0;
        levelQs.forEach(q => {
          const w = q.weight || 1;
          total += w;
          earned += w * scoreQuestion(q, answers[q.id]);
        });
        row[key] = total > 0 ? Math.round((earned / total) * 100) : 0;
      } else {
        // Answers can't be matched to questions — use the stored level score
        row[key] = null;
      }
    });
    return row;
  });
}

function getHeatColor(score) {
  if (score === null || score === undefined) return { bg: 'bg-muted', text: 'text-muted-foreground' };
  if (score >= 80) return { bg: 'bg-teal-500', text: 'text-white' };
  if (score >= 60) return { bg: 'bg-teal-300', text: 'text-teal-900' };
  if (score >= 40) return { bg: 'bg-amber-300', text: 'text-amber-900' };
  if (score >= 20) return { bg: 'bg-orange-400', text: 'text-white' };
  return { bg: 'bg-red-500', text: 'text-white' };
}

export default function ThemeHeatmap({ assessments, currentLevel, organisationId }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [warning, setWarning] = useState('');
  const selectedAssessment = assessments?.[0] || null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setWarning('');
    (async () => {
      try {
        if (organisationId === undefined || organisationId === null) throw new Error('Select an organisation to view assessment readiness');
        let frameworkId = selectedAssessment?.framework_version_id;
        if (selectedAssessment && !frameworkId) throw new Error('This assessment has no saved framework version and cannot be scored safely');
        if (!frameworkId) {
          const current = await base44.framework.current();
          frameworkId = current?.id;
        }
        if (!frameworkId) throw new Error('No published assessment framework is available');
        let qs = await base44.framework.questions(frameworkId);
        if (!Array.isArray(qs)) qs = qs?.questions || [];
        if (!qs.length) throw new Error('No questions are available for this assessment version');
        if (!cancelled) {
          setQuestions(qs);
          const answers = selectedAssessment?.answers || {};
          const ids = new Set(qs.map(q => q.id));
          if (selectedAssessment && Object.keys(answers).length > 0 && !Object.keys(answers).some(id => ids.has(id))) setWarning('Saved answers do not match this framework version, so scores are shown as unavailable until the pinned questions are restored.');
        }
      } catch (e) {
        if (!cancelled) { setQuestions([]); setError(e.message || 'Unable to load the assessment framework'); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [organisationId, selectedAssessment?.id, selectedAssessment?.framework_version_id, reload]);

  const data = buildHeatmapData(assessments, questions);
  const nextLevel = Math.min((currentLevel ?? 0) + 1, 3);

  if (loading) return (
    <div className="bg-card border border-border rounded-xl p-5" role="status" aria-live="polite">Loading theme readiness…</div>
  );

  if (error) return (
    <div className="bg-card border border-border rounded-xl p-5" role="alert">
      <p className="text-sm text-muted-foreground">{error}</p>
      <button type="button" className="mt-3 px-3 py-2 rounded border border-border text-sm" onClick={() => setReload(value => value + 1)}>Retry</button>
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-xl p-3 sm:p-5 shadow-sm">
      <div className="mb-3 sm:mb-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div>
          <h2 className="text-xs sm:text-sm font-semibold text-foreground">Theme Readiness Heatmap</h2>
          <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">
            Score per theme across levels — focus on red/orange for Level {nextLevel}
          </p>
          {warning && <p className="mt-2 text-xs text-amber-700" role="status">{warning}</p>}
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <span className="text-[10px] sm:text-xs text-muted-foreground">Low</span>
          {['bg-red-500', 'bg-orange-400', 'bg-amber-300', 'bg-teal-300', 'bg-teal-500'].map((c, i) => (
            <span key={i} className={`w-2.5 h-2.5 sm:w-4 sm:h-4 rounded-sm ${c}`} />
          ))}
          <span className="text-[10px] sm:text-xs text-muted-foreground">High</span>
        </div>
      </div>

      <div className="overflow-x-auto -mx-3 sm:mx-0">
        <table className="w-full text-xs sm:text-sm" role="grid" aria-label="Theme readiness heatmap">
          <thead>
            <tr>
              <th className="text-left text-[10px] sm:text-xs font-medium text-muted-foreground pb-2 pr-2 sm:pr-4 w-24 sm:w-40">Theme</th>
              {LEVELS.map((l, idx) => (
                <th key={l.key} className="text-center text-[10px] sm:text-xs font-medium text-muted-foreground pb-2 px-1 sm:px-2">
                  <span className="block">{l.label.split(' ')[0]}</span>
                  {nextLevel === idx + 1 && (
                    <span className="text-[9px] sm:text-[10px] text-amber-500 font-semibold">▲ Next</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="space-y-1">
            {data.length === 0 && (
              <tr>
                <td colSpan={4} className="py-4 text-center text-[10px] sm:text-xs text-muted-foreground">
                  No assessment data yet.
                </td>
              </tr>
            )}
            {data.map(row => (
              <tr key={row.theme}>
                <td className="pr-2 sm:pr-4 py-1.5 text-[10px] sm:text-xs font-medium text-foreground whitespace-nowrap">{row.theme}</td>
                {LEVELS.map(l => {
                  const score = row[l.key];
                  const { bg, text } = getHeatColor(score);
                  return (
                    <td key={l.key} className="px-1 sm:px-2 py-1.5 text-center">
                      <div
                        className={`inline-flex items-center justify-center rounded-md w-10 sm:w-16 h-6 sm:h-8 text-[9px] sm:text-xs font-bold ${bg} ${text}`}
                        title={`${row.theme} — ${l.label}: ${score === null ? 'N/A' : score + '%'}`}
                        aria-label={`${row.theme} ${l.label} score: ${score === null ? 'not applicable' : score + '%'}`}
                      >
                        {score === null ? '—' : `${score}%`}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 sm:mt-4 pt-2 sm:pt-3 border-t border-border flex flex-wrap gap-2 sm:gap-3 text-[10px] sm:text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><span className="w-2 h-2 sm:w-3 sm:h-3 rounded-sm bg-red-500 inline-block" /> 0–19%</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 sm:w-3 sm:h-3 rounded-sm bg-orange-400 inline-block" /> 20–39%</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 sm:w-3 sm:h-3 rounded-sm bg-amber-300 inline-block" /> 40–59%</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 sm:w-3 sm:h-3 rounded-sm bg-teal-300 inline-block" /> 60–79%</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 sm:w-3 sm:h-3 rounded-sm bg-teal-500 inline-block" /> 80–100%</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 sm:w-3 sm:h-3 rounded-sm bg-muted inline-block border border-border" /> N/A</span>
      </div>
    </div>
  );
}
